import { inject, computed, type ComputedRef } from 'vue';

import type { HttpClientInstance } from '@thehotelsnetwork/http-client';

import apiClient from '@/modules/ApiClient';
import { useAccountsService } from '@/modules/Accounts/services/AccountsService';

// Injection keys
const TEAM_API_CLIENT_KEY = Symbol('teamApiClient');
const TEAM_ACCOUNT_ID_KEY = Symbol('teamAccountId');

export interface TeamDependencies {
  apiClient: HttpClientInstance;
  accountId: ComputedRef<string>;
}

/**
 * Provides dependencies for Team module composables
 * Used internally by useTeam and useTeamMember
 */
export function useTeamDependencies(): TeamDependencies {
  // Try to inject dependencies first (for flexibility in tests or custom contexts)
  const injectedApiClient = inject<HttpClientInstance | null>(
    TEAM_API_CLIENT_KEY,
    null,
  );
  const injectedAccountId = inject<ComputedRef<string> | null>(
    TEAM_ACCOUNT_ID_KEY,
    null,
  );

  // Fall back to default dependencies if not injected
  const accountsService = useAccountsService();

  return {
    apiClient: injectedApiClient ?? apiClient,
    accountId: injectedAccountId ?? computed(() => accountsService.account?.id ?? ''),
  };
}

// Export keys for testing purposes
export { TEAM_API_CLIENT_KEY, TEAM_ACCOUNT_ID_KEY };
